import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SourceViewer from './SourceViewer';
import InvoiceSummary from './InvoiceSummary';
import InvoiceLines from './InvoiceLines';
import ApprovePanel from './ApprovePanel';
import CorrectReading from './CorrectReading';
import { StatePill } from './InvoiceList';
import { useDemoTools } from './DemoControls';
import { INVOICE_DECIDERS, mayDo, useExperience } from '../experience/experience';
import useItemDetails from './useItemDetails';
import { useInvoicesBase } from './basePath';
import { getInvoiceDetail, resolveTask } from './api';
import { buildLineViews, openTaskCount } from './invoiceStatus';

export default function InvoiceReview({ invoiceId }) {
  const navigate = useNavigate();
  const base = useInvoicesBase();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [pane, setPane] = useState('details');
  const demo = useDemoTools();
  const exp = useExperience();
  const canDecide = mayDo(exp, INVOICE_DECIDERS);    // staff upload; a manager decides (pipeline_api.py)

  const load = useCallback(async () => {
    try {
      setDetail(await getInvoiceDetail(invoiceId));
      setError(null);
    } catch (e) {
      setError(e.status === 404 ? 'This invoice is no longer here. It may have been removed.' : e.message);
    }
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  const invoice = detail?.invoice;
  const tasks = detail?.review_tasks;
  const views = useMemo(() => (invoice ? buildLineViews(invoice, tasks) : []), [invoice, tasks]);
  const ids = useMemo(() => views.map((v) => v.line.item_id), [views]);
  const { items, reload } = useItemDetails(ids);
  const itemNames = useMemo(() => Object.fromEntries(Object.entries(items).map(([k, v]) => [k, v.name])), [items]);

  // One task at a time: the server serializes resolutions per invoice anyway. The lines stay locked until the reload
  // has landed: one decision can settle other lines too (a category is per item), so the lines drawn before the reload
  // may offer a task the server has already closed, and a tap on it answers 409 "Task already resolved".
  const inFlight = useRef(false);
  const resolveMany = async (jobs) => {
    const todo = jobs.filter((j) => j.task);
    if (!todo.length || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const failed = [];
    for (let k = 0; k < todo.length; k += 1) {
      if (todo.length > 1) setProgress(`Saving ${k + 1} of ${todo.length}…`);
      try {
        // eslint-disable-next-line no-await-in-loop
        await resolveTask(todo[k].task.id, todo[k].body);
      } catch (e) {
        failed.push(e.message);
      }
    }
    setProgress(failed.length ? `${failed.length} could not be saved: ${failed[0]}` : null);
    try {
      await load();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (error) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="invoice-review-error">
        <p className="text-ink mb-4">{error}</p>
        <Link to={base} className="underline text-primary-700" data-testid="invoice-review-back">Back to invoices</Link>
      </main>
    );
  }
  if (!invoice) return <main className="max-w-3xl mx-auto px-4 py-12 text-ink-muted" data-testid="invoice-review-loading">Loading invoice…</main>;

  const open = openTaskCount(tasks);
  const extractionOpen = (tasks || []).some((t) => t.status === 'open' && t.type === 'extraction');
  const paneCls = (name) => (pane === name ? '' : 'hidden md:block');

  return (
    <main className="min-h-screen bg-surface-muted" data-testid="invoice-review">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <header className="flex flex-wrap items-center gap-3">
          <Link to={base} data-testid="invoice-review-back"
            className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-lg text-ink-muted hover:bg-surface-sunken">
            <ArrowLeft className="w-4 h-4" /> All invoices
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl text-ink mr-auto" data-testid="invoice-review-title">
            {invoice.supplier_name || 'Invoice'} <span className="text-ink-subtle">· {invoice.invoice_no || 'no number'}</span>
          </h1>
          <StatePill state={invoice.state} />
        </header>

        <div role="tablist" aria-label="Invoice view" className="flex md:hidden rounded-full border border-surface-border bg-surface p-1">
          {[['details', 'Details'], ['document', 'Document']].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={pane === id} onClick={() => setPane(id)} data-testid={`review-pane-tab-${id}`}
              className={`flex-1 min-h-[44px] rounded-full text-sm font-medium ${pane === id ? 'bg-primary-500 text-surface' : 'text-ink-muted'}`}>{label}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className={`md:sticky md:top-20 md:h-[calc(100vh-6.5rem)] ${paneCls('document')}`}>
            <SourceViewer documentId={invoice.document_id} />
          </div>
          <div className={`space-y-5 min-w-0 ${paneCls('details')}`}>
            <InvoiceSummary invoice={invoice} />
            {canDecide && <CorrectReading invoice={invoice} startOpen={extractionOpen} onChanged={load} />}
            <ApprovePanel invoice={invoice} openTasks={open} items={items}
              extractionTask={extractionOpen} reloadItems={reload} onChanged={load}
              canRemove={!!demo.info} onRemoved={() => navigate(base)}
              costingLink={exp.nav.has('nav-cut-cost-price-impact')} canDecide={canDecide} />
            <InvoiceLines key={invoice.id} views={views} itemNames={itemNames} onResolveMany={resolveMany} busy={busy} progress={progress} canDecide={canDecide} />
          </div>
        </div>
      </div>
    </main>
  );
}
